import { $, doAction } from '@situation/setdesign.util';
import { selectors } from '../constants';

/**
 * View layer for enhancing and updating the Gravity Form fields.
 */
export default class FormView {
    /**
     * @param {Object} options
     * @param {string} options.formSelector - CSS selector for the Gravity Form
     * @param {Function} options.onDateClick - Callback(prefIndex) when a date field is clicked
     * @param {Function} options.onSectionChange - Callback(prefIndex, section)
     * @param {Function} options.onPeopleChange - Callback(prefIndex, people)
     */
    constructor({
        formSelector, onDateClick, onSectionChange, onPeopleChange,
    }) {
        this.formSelector = formSelector;
        this.onDateClick = onDateClick;
        this.onSectionChange = onSectionChange;
        this.onPeopleChange = onPeopleChange;
        this._cache = {};
    }

    /**
     * Performs initial, one-time DOM restructuring into an accordion.
     * This method is idempotent and safe to call multiple times.
     */
    setupLayout() {
        this.formEl = document.querySelector(this.formSelector);

        if (!this.formEl) return;

        this._setupAccordions();
    }

    /**
     * Caches fields and binds fresh event listeners to the current form DOM.
     * This should be called every time the form is re-rendered.
     */
    bindUI() {
        if (!this.formEl) return;

        // Cache field lists by preference index
        this._groupFieldsByIndex();
        // Bind listeners
        Object.entries(this._cache).forEach(([prefIdx, { dateInput, sectionSelect, peopleInput }]) => {
            const idx = Number(prefIdx);
            // Date field: open calendar modal
            const dateEl = dateInput;
            dateEl.readOnly = true;
            dateEl.addEventListener('click', () => this.onDateClick(idx));
            // Section select: on change
            const sectionEl = sectionSelect;
            sectionEl.disabled = true;
            sectionEl.addEventListener('change', (e) => {
                this.onSectionChange(idx, e.target.value);
                const peopleEl = peopleInput;
                peopleEl.disabled = !e.target.value;
            });

            // People input: on change
            const peopleEl = peopleInput;
            peopleEl.disabled = true;
            peopleEl.addEventListener('change', (e) => this.onPeopleChange(idx, Number(e.target.value)));
        });
    }

    /**
     * NEW (jQuery): Finds Gravity Form sections and restructures the DOM into an accordion.
     * Now checks if the accordion has already been built.
     * @private
     */
    _setupAccordions() {
        const $gformBody = $(this.formSelector).find('.gform_fields');
        const expanded = $(this.formSelector).find('.gfield_error').length;
        // If there are no sections to process, or if the accordion already exists, bail.
        if (!$gformBody.length || $gformBody.find('.accordion').length) return;

        const $sections = $gformBody.find('.gfield--type-section');
        if (!$sections.length) return;

        // Create wrapper and prepend
        const $accordion = $('<div class="accordion" />').prependTo($gformBody);
        // Build each panel
        $sections.each((i, sectionEl) => {
            const $section = $(sectionEl);
            const titleText = $section.find('.gsection_title').text() || `Section ${i + 1}`;
            // Panel shell
            const $panel = $('<div class="accordion-panel" />');
            if (expanded) {
                $panel.addClass('accordion-panel--expanded');
            }

            const $heading = $('<h3 class="accordion-panel__heading heading heading--medium" />');
            const $button = $(
                '<button type="button" class="accordion-panel__button" aria-expanded="false" />',
            ).html(html`
                <span class="accordion-panel__headline">
                    <span class="accordion-panel__headline__text">${titleText}</span>
                </span>
                <svg
                    class="accordion-panel__icon"
                    data-icon-name="plus"
                    data-expanded-icon-name="minus"
                    aria-hidden="true"
                    focusable="false">
                    <use class="icon-use" xlink:href="#icon-plus"></use>
                </svg>
            `);
            const $content = $('<div class="accordion-panel__content" aria-hidden="true" />');
            // Move all fields until the next section into our content
            const $fields = $section.nextUntil($sections);
            $fields.appendTo($content);
            // Assemble
            $heading.append($button);
            $panel.append($heading, $content);
            $accordion.append($panel);
            // Remove the original section header
            $section.remove();
        });

        // If you have other init logic (e.g. icons sprite), still call it
        doAction('initializeAccordion', $accordion.get(0));
    }

    /**
     * Updates a single preference row in the DOM.
     * @param {number} index
     * @param {Object} preference
     */
    updatePreferenceRow(index, preference) {
        const { dateInput, sectionSelect, peopleInput } = this._cache[index] || {};
        if (!dateInput) return;
        // Date field
        if (preference.event) {
            dateInput.value = preference.event.date;
            sectionSelect.disabled = false;
            // Populate section options preserving any placeholder
            const placeholderOpt = sectionSelect.querySelector('.gf_placeholder');
            sectionSelect.innerHTML = '';

            if (placeholderOpt) {
                sectionSelect.appendChild(placeholderOpt);
            }

            preference.event.pricing.forEach(({ section, price }) => {
                const opt = document.createElement('option');
                const label = price === '*' ? price : `$${price}`;
                opt.value = section;
                opt.textContent = `${section} (${label})`;

                if (preference.section === section) opt.selected = true;

                sectionSelect.appendChild(opt);
            });
        } else {
            dateInput.value = '';
            sectionSelect.disabled = true;
            sectionSelect.innerHTML = '';
            peopleInput.disabled = true;
            peopleInput.value = '';
        }
        // Section chosen
        if (preference.section) {
            sectionSelect.value = preference.section;
            peopleInput.disabled = false;
        } else if (preference.event) {
            // Only clear people input if we have an event but no section
            peopleInput.disabled = true;
            peopleInput.value = '';
        }
        // Number of people
        if (preference.people != null) {
            peopleInput.value = preference.people;
        }
    }

    /**
     * Toggles a class on the top-level form container.
     * @param {boolean} show - Whether to add or remove the class.
     */
    toggleStarPriceClass(show) {
        if (!this.formEl) return;

        this.formEl.classList.toggle(selectors.starPriceClass, show);
    }

    /**
     * Groups form fields by their data-preference-index attribute.
     * Caches references to inputs for quick updates.
     * @private
     */
    _groupFieldsByIndex() {
        this._cache = {};
        let index = 1;

        while (true) {
            const headingField = this.formEl.querySelector(`.preference_${index}.preference_heading`);
            const dateField = this.formEl.querySelector(`.preference_${index}.preference_date`);
            const sectionField = this.formEl.querySelector(`.preference_${index}.preference_section`);
            const peopleField = this.formEl.querySelector(`.preference_${index}.preference_people`);
            if (!dateField || !sectionField || !peopleField) {
                break;
            }
            // only wrap if there’s a heading and it’s not already inside a group-tickets-preference-col
            if (headingField && !headingField.closest('.group-tickets-preference-col')) {
                const groupDiv = document.createElement('div');
                groupDiv.className = 'group-tickets-preference-col';
                headingField.parentNode.insertBefore(groupDiv, headingField);
                [headingField, dateField, sectionField, peopleField].forEach((el) => groupDiv.appendChild(el));
            }

            // cache inputs for later
            this._cache[index] = {
                dateInput: dateField.querySelector('input'),
                sectionSelect: sectionField.querySelector('select'),
                peopleInput: peopleField.querySelector('input'),
            };

            index += 1;
        }
    }
}
